import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { PAGEVIEW_EVENT, analyticsBeforeSend } from './funnel';
import { reportAnalytics } from './transport';

const RECENT_PAGEVIEW_KEY = 'dd_last_pageview';

/**
 * StrictMode remounts this component in development and resets the ref, which
 * would send the view twice. A same-path send a few milliseconds later is that
 * remount; a later visit to the same path is a new view.
 */
function claimPageview(path: string): boolean {
  const now = Date.now();
  try {
    const raw = sessionStorage.getItem(RECENT_PAGEVIEW_KEY);
    if (raw) {
      const previous = JSON.parse(raw) as { path?: string; at?: number };
      if (previous.path === path && typeof previous.at === 'number' && now - previous.at < 1000) {
        return false;
      }
    }
    sessionStorage.setItem(RECENT_PAGEVIEW_KEY, JSON.stringify({ path, at: now }));
  } catch {
    // sessionStorage can be unavailable. The ref below still covers one mount.
  }
  return true;
}

/**
 * Reports one page view per public path.
 *
 * Mounted once beside the router. The ref remembers the last path so a
 * re-render or a query-string change does not send the view again. A route the
 * allow-list drops — a repository, a share token, an admin page — sends nothing
 * and clears the memory, so coming back to a public page is counted.
 */
export function PublicAnalytics() {
  const location = useLocation();
  const reportedPath = useRef<string | null>(null);

  useEffect(() => {
    const href = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    const decision = analyticsBeforeSend({ type: 'pageview', url: href });
    if (!decision) {
      reportedPath.current = null;
      return;
    }

    let path: string;
    try {
      path = new URL(decision.url).pathname;
    } catch {
      return;
    }
    if (reportedPath.current === path) return;
    if (!claimPageview(path)) return;
    reportedPath.current = path;
    reportAnalytics({ name: PAGEVIEW_EVENT, path });
  }, [location.pathname, location.search, location.hash]);

  return null;
}
